import { FieldArray, FieldArrayRenderProps, Form, Formik, FormikHelpers, FormikProps } from 'formik';
import * as Yup from 'yup';
import { Grid, Hidden } from '@material-ui/core';
import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import Tooltip from '@cpg-its/ads.common.tooltip';
import Field from '@cpg-its/ads.form.field';
import Banner from '@cpg-its/ads.containers.banner';
import { DemographicAppKeys, DemographicsContext } from '../../../global/models/demographics-model';
import { TcsmStates } from '../../../root/tcsm-model';
import { AnnualEnrollmentProps } from '../../arc/models/arc-epi-props';
import { AEPlanType, AnnualEnrollmentState, AERelationshipCode, SelfText, AnnualEnrollmentContext, AnnualEnrollmentKey } from '../models/annual-enrollment-models';
import { FormWrapper } from '../../../global/components/form-wrapper';
import { AnnualEnrollmentFormValues } from '../models/annual-enrollment-form-models';
import { YesNoIndicators } from '../../../global/models/api-model';
import { CustomField } from '../../../global/components/form-element/custom-field';
import { RadioTable } from '../../../global/components/form-element/radio-table';
import { getDependentCountByRelationship, getOfferingsOptions, getRelationshipLabel, getTierLabel, isDependentAgedOut, isDependentAgingOut, isDependentHealthImputedIncome, isPlanSelectionAvailable } from '../helpers/annual-enrollment-helpers';
import { ConfirmCancelModalContent } from '../../../global/components/confirm-cancel-dialog-content';
import { TcsmModal } from '../../../global/components/tcsm-modal';
import { DividerSection } from '../../../global/components/layout/divider-section';
import { FormGroup } from '../../../global/components/layout/form-group';
import { ContentSpacer } from '../../../global/components/layout/generic-content-spacer';
import { tcsmColors } from '../../../global/constants/theme/theme-constants/colors';
import { AnnualEnrollmentReview } from './annual-enrollment-review';
import { AnnualEnrollmentSummary } from './annual-enrollment-summary';
import { getAnnualEnrollmentTierCalc } from '../../../global/ducks/mlps-api';
import { setAECalcTiers } from '../annual-enrollment-slice';
import { coverageTableStyles } from '../../../global/constants/theme/styles/tables/coverage-table';
import { FormField } from '../../../global/components/form-element/form-field';
import { LabelSettingProps } from '../../../global/models/epi-model';
import { EnrollmentErrors, EnrollmentTypes } from './annual-enrollment-errors';
import { PageHeader } from '../../../global/components/layout/page-header';
import { SideBarContent } from '../../../global/components/layout/sidebar-content';
import { getRemainingCharacterCounter } from '../../arc/features/mid-year/mid-year-helpers';
import { MaxCharacter255 } from '../../arc/constants/constant-strings';
import { useGpsType } from '../../../global/hooks/useGpsType';
import { GpsType } from '../../../global/components/layout/layout-models';
import { GuidedProcessSupportBlock } from '../../../global/components/guided-process-support-block';
import { downloadFileFromApi } from '../../../global/helpers/file-helpers';
import { ApiEndpoint } from '../../../global/constants/api-endpoints';
import { buttonStyles } from '../../../global/constants/theme/styles/components/buttonstyles';
import { ExternalLink } from '../../../global/components/external-link-modal';
import { firstDayOfNextCalendarYear, getFormattedDate } from '../../../global/helpers/date-helpers';
import { WysiwygWithReactLink } from '../../../global/components/wysiwyg-with-react-link';
import { getRequiredText } from '../../../global/helpers/transformer';
import { parseLov } from '../../../global/helpers/form-data-helper';
import { isNotApplicableRate } from '../../../global/helpers/format-helpers';
import { DeclinedValue } from '../../arc/features/new-hire-enrollment/enrollment/models/coverage-models';

interface GroupPersonCombo {
	group: AEPlanType;
	person: string;
}

interface Props {
	labelSettings: LabelSettingProps;
	annualEnrollmentProps: AnnualEnrollmentProps;
	coverageDisplay: JSX.Element;
	medicalExpired: boolean;
	dentalExpired: boolean;
	previousStepAction: (formikProps: FormikProps<AnnualEnrollmentFormValues>) => void;
	showReview: boolean;
	onSubmit: (values: AnnualEnrollmentFormValues, savedValues: AnnualEnrollmentFormValues, formikHelpers: FormikHelpers<AnnualEnrollmentFormValues>) => any;
}

const comparisonDocLink = '/document/static_group/group_comparison/client/';

export function AnnualEnrollmentForm(props: Props) {
	const medicalDeclineCoverageReasonOther = 'other';

	const classes = coverageTableStyles();
	const app = useContext(DemographicsContext);

	const { annualEnrollmentProps, labelSettings, medicalExpired, dentalExpired, previousStepAction, onSubmit, showReview, coverageDisplay } = props;

	const AEState = useSelector<TcsmStates, AnnualEnrollmentState>(state => state.annualEnrollment[app]);
	const medicalEnrollment = AEState.AEInfo?.enrollments_base?.M[0];
	const dentalEnrollment = AEState.AEInfo?.enrollments_base?.D[0];
	const hasMspPlan = annualEnrollmentProps?.mspCodes?.split(',')?.filter(a => a && medicalEnrollment?.code?.includes(a))?.length > 0;
	const dispatch = useDispatch();
	const buttonClasses = buttonStyles();
	const [removeSelfDialogGroup, setRemoveSelfFlag] = useState(AEPlanType.None);
	const [reselectSelfCombo, setReselectSelfFlag] = useState<GroupPersonCombo>(null);
	const gpsItem = useGpsType(showReview ? GpsType.AE_REVIEW : GpsType.AE_COVERAGE, app);
	const calculateTiers = (groupName: AEPlanType, selectedPeople: string[]) => {
		const groupId = groupName === AEPlanType.Medical ? AEState.AEInfo?.ae_defaults?.medical_group_id : AEState.AEInfo?.ae_defaults?.dental_group_id;
		const tierCount = AEState.AEInfo?.offerings[groupName]?.available_tiers?.length;
		const spouseCount = getDependentCountByRelationship(selectedPeople, AEState.AEInfo?.eligible_dependents, [AERelationshipCode.Spouse, AERelationshipCode.DomesticPartner]);
		const childCount = getDependentCountByRelationship(selectedPeople, AEState.AEInfo?.eligible_dependents, [AERelationshipCode.Child, AERelationshipCode.StepChild, AERelationshipCode.Disabled]);

		dispatch(getAnnualEnrollmentTierCalc(groupId, tierCount, spouseCount, childCount, (response) => {
			dispatch(setAECalcTiers(response, groupName, app));
		}));
	};
	const token = useSelector<TcsmStates, string>(state => state.authenticatedState?.accessToken);
	const personGuid = useSelector<TcsmStates, string>(state => app === DemographicAppKeys.myCPG ? state?.personalInfo?.myCPG?.currentPerson?.person_guid : state?.personalInfo?.arc?.currentPerson?.person_guid);
	const downloadPdf = (downloadLink: string, fileName: string) => {
		downloadFileFromApi(`${ApiEndpoint.mlps}${downloadLink}${personGuid}`, token, fileName);
	};

	const isAnnualEnrollment = useContext(AnnualEnrollmentContext) === AnnualEnrollmentKey.annualEnrollment;
	const medicalDeclineReasonLov = useSelector<TcsmStates, any>(state => state.lovProcState?.mlpsLov?.medicalDeclineReason);
	const declinedMedicalCoverageReasonOptions = medicalDeclineReasonLov ? parseLov(medicalDeclineReasonLov) : [];
	const showNotApplicableRate = AEState.AEInfo?.offerings?.M?.plans?.some(x => Object.keys(x?.rates_by_tier)?.some(k => isNotApplicableRate(x?.rates_by_tier[k]))) ||
		AEState.AEInfo?.offerings?.D?.plans?.some(x => Object.keys(x?.rates_by_tier)?.some(k => isNotApplicableRate(x?.rates_by_tier[k])));


	const labelSetting = useSelector<TcsmStates, LabelSettingProps>(state => state.epiProps.labelSettingState.epiContent);

	const getInitialCoverageValues = (typeKey: AEPlanType, expired: boolean) => {
		const coverage: string[] = [];

		if (!expired && AEState.AEInfo?.enrollments_base) {
			if (AEState.AEInfo?.enrollments_base[typeKey]?.length > 0) {
				coverage.push(SelfText);
			}

			const eligibleDependents = AEState.AEInfo?.eligible_dependents;
			for (let i = 0; i < eligibleDependents?.length ?? 0; i += 1) {
				const dependentGuid = eligibleDependents[i]?.person?._guid;
				if (AEState.AEInfo?.dep_enrollments_base[dependentGuid][typeKey] === YesNoIndicators.Yes && eligibleDependents[i]?.dep_aged_out === YesNoIndicators.No) {
					coverage.push(dependentGuid);
				}
			}
		}

		return coverage;
	};

	const initialValues: AnnualEnrollmentFormValues = {
		[AEPlanType.Medical]: getInitialCoverageValues(AEPlanType.Medical, medicalExpired),
		[AEPlanType.Dental]: getInitialCoverageValues(AEPlanType.Dental, dentalExpired),

		medicalPlan: medicalExpired ? '' : AEState?.AEInfo?.enrollments_base?.M[0]?.code || '',
		dentalPlan: dentalExpired ? '' : AEState?.AEInfo?.enrollments_base?.D[0]?.code || '',

		agreement: false,

		declinedMedicalCoverageReason: '',
		declinedMedicalCoverageReasonOther: '',
	};

	const confirmUnselectSelf = (groupName: AEPlanType, formProps: FormikProps<AnnualEnrollmentFormValues>) => {
		formProps.setFieldValue(groupName, []);
		formProps.setFieldValue(groupName === AEPlanType.Medical ? 'medicalPlan' : 'dentalPlan', DeclinedValue);
		setRemoveSelfFlag(AEPlanType.None);

		// remove calculated tiers when unselect self
		dispatch(setAECalcTiers(null, groupName, app));
	};

	const confirmSelectSelf = (selectionProps: GroupPersonCombo, formProps: FormikProps<AnnualEnrollmentFormValues>) => {
		const newValues = [SelfText, selectionProps?.person];

		formProps.setFieldValue(selectionProps?.group, newValues);
		setReselectSelfFlag(null);

		calculateTiers(selectionProps?.group, newValues);
	};

	const onCheckboxChange = (e, groupName: AEPlanType, fieldArrayProps: FieldArrayRenderProps, formProps: FormikProps<AnnualEnrollmentFormValues>) => {
		const values = formProps.values[groupName];
		const { value } = e.target;
		if (e.target.checked) {
			if (value !== SelfText && !values.includes(SelfText)) {
				setReselectSelfFlag({ group: groupName, person: value });
			} else {
				fieldArrayProps.push(value);
				calculateTiers(groupName, [...values, value]);
			}
		} else if (value === SelfText) {
			setRemoveSelfFlag(groupName);
		} else {
			fieldArrayProps.remove(values.indexOf(value));
			calculateTiers(groupName, values.filter(x => x !== value));
		}
	};

	const onCoverageChange = (e, groupName: AEPlanType, formProps: FormikProps<AnnualEnrollmentFormValues>) => {
		const { value } = e.target;
		if (!value || value === DeclinedValue) {
			setRemoveSelfFlag(groupName);
		} else {
			formProps.setFieldValue(groupName === AEPlanType.Medical ? 'medicalPlan' : 'dentalPlan', value);
		}
	};

	const getSecondaryLabel = (checked: boolean, groupName: AEPlanType, value: string) => {
		if (checked && !initialValues[groupName]?.includes(value)) {
			return [annualEnrollmentProps?.addedText, tcsmColors.green];
		}

		if (!checked && initialValues[groupName]?.includes(value)) {
			return [annualEnrollmentProps?.removedText, tcsmColors.red];
		}

		return null;
	};

	const renderCoverage = (formProps: FormikProps<AnnualEnrollmentFormValues>, groupName: AEPlanType, groupLabel: string) => {

		const selfChecked = formProps?.values[groupName]?.includes(SelfText);
		const selfSecondaryLabelOptions = getSecondaryLabel(selfChecked, groupName, SelfText);
		return (
			<>
				<FieldArray
					name={groupName}
					render={(fieldArrayProps) => (
						<>
							<ContentSpacer>
								<h4>{groupLabel}</h4>
							</ContentSpacer>
							<Field
								id={`${groupName}-self`}
								name={groupName}
								label={annualEnrollmentProps?.selfOptionText || 'Self'}
								secondaryLabel={selfSecondaryLabelOptions && selfSecondaryLabelOptions[0]}
								secondaryLabelColor={selfSecondaryLabelOptions && selfSecondaryLabelOptions[1]}
								component='checkBox'						
								value={SelfText}
								checked={selfChecked}
								checkBoxGroup
								onChange={(e) => onCheckboxChange(e, groupName, fieldArrayProps, formProps)}
							/>
							{AEState.AEInfo?.eligible_dependents?.map(dep => {
								const dependentChecked = formProps?.values[groupName]?.includes(dep?.person?._guid);
								const dependentSecondaryLabelOptions = getSecondaryLabel(dependentChecked, groupName, dep?.person?._guid);
								const agedOut = isDependentAgedOut(dep);
								const agingout = isDependentAgingOut(dep);

								return (
									<>
										<Field
											id={`${groupName}-${dep.person?._guid}`}
											key={`${groupName}-${dep.person?._guid}`}
											name={groupName}
											label={`${dep?.person?.first} ${dep?.person?.middle} ${dep?.person?.last}  ${dep?.person?.suffix}`}
											secondaryLabel={dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[0]}
											secondaryLabelColor={dependentSecondaryLabelOptions && dependentSecondaryLabelOptions[1]}
											description={getRelationshipLabel(dep?.relationship_code, annualEnrollmentProps)}
											component='checkBox'											
											value={dep?.person?._guid}
											disabled={agedOut}
											checked={!agedOut && dependentChecked}
											checkBoxGroup
											onChange={(e) => onCheckboxChange(e, groupName, fieldArrayProps, formProps)}
										/>
										{
											agingout &&
											<p className='red-text'><strong>{annualEnrollmentProps?.ineligibleDependentsWarning?.replace('{date}', getFormattedDate(firstDayOfNextCalendarYear()?.toString()))}</strong></p>
										}
										{
											agedOut &&
											<p className='red-text'><strong>{annualEnrollmentProps?.dependentAgedOutWarningMessage}</strong></p>
										}
									</>
								);
							})}
						</>
					)}
				/>

				<TcsmModal
					open={removeSelfDialogGroup !== AEPlanType.None}
					handleClose={() => setRemoveSelfFlag(AEPlanType.None)}>

					<ConfirmCancelModalContent
						description={annualEnrollmentProps?.unselectSelfModalMessage}
						confirmAction={{
							action: () => confirmUnselectSelf(removeSelfDialogGroup, formProps),
							label: annualEnrollmentProps?.unselectSelfConfirmText,
						}}
						cancelAction={{
							action: () => setRemoveSelfFlag(AEPlanType.None),
							label: annualEnrollmentProps?.unselectSelfCancelText,
						}}
					/>
				</TcsmModal>

				<TcsmModal
					open={reselectSelfCombo && reselectSelfCombo.group !== AEPlanType.None}
					handleClose={() => setReselectSelfFlag(null)}>

					<ConfirmCancelModalContent
						description={annualEnrollmentProps?.selectSelfModalMessage}
						confirmAction={{
							action: () => confirmSelectSelf(reselectSelfCombo, formProps),
							label: annualEnrollmentProps?.selectSelfConfirmText,
						}}
						cancelAction={{
							action: () => setReselectSelfFlag(null),
							label: annualEnrollmentProps?.selectSelfCancelText,
						}}
					/>
				</TcsmModal>
			</>
		);
	};

	const getPlanLabels = (planType: AEPlanType) => {
		const primaryLabel = planType === AEPlanType.Medical ? annualEnrollmentProps?.medicalPlansLabel : annualEnrollmentProps?.dentalPlansLabel;

		if (!AEState.AEInfo?.offerings) return null;

		return (
			<div className='coverage-table-row header-row'>
				<div className='coverage-table-row-cell'><h4>{primaryLabel}</h4></div>
				<Hidden xsDown>
					{AEState.AEInfo?.offerings[planType]?.available_tiers.map(tier => <div className='coverage-table-row-cell tier-cell' key={tier}><h5>{getTierLabel(tier, annualEnrollmentProps)}</h5></div>)}
				</Hidden>
			</div>
		);
	};

	const renderComments = (formProps: FormikProps<AnnualEnrollmentFormValues>) => {
		if (showReview || app === DemographicAppKeys.myCPG) return null;

		return (
			<DividerSection removeDivider>
				<FormGroup>
					<ContentSpacer>

						<FormField
							name='memo'
							label={annualEnrollmentProps.myNotesLabel}
							component='input'
							multiline
							rows={5}
							touched={formProps.touched}
							errors={formProps.errors}
						/>
						<Grid container justifyContent='flex-end'>
							<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.memo?.length)}/255 ${annualEnrollmentProps.characterRemainingText || ''}`}</p>
						</Grid>
					</ContentSpacer>

					<p><strong>{annualEnrollmentProps.specialRequestsLabel}</strong></p>
					<p>{annualEnrollmentProps.specialRequestsDescription}</p>
					<FormField
						name='specialRequests'
						component='input'
						multiline
						rows={5}
						touched={formProps.touched}
						errors={formProps.errors}
					/>
					<Grid container justifyContent='flex-end'>
						<p className='input-caption'>{`${getRemainingCharacterCounter(formProps?.values?.specialRequests?.length)}/255 ${annualEnrollmentProps.characterRemainingText || ''}`}</p>
					</Grid>
				</FormGroup>
			</DividerSection>
		);
	};

	const renderDependentAdultChildWarning = () => {
		const anyDependentHealthImputedIncome = AEState.AEInfo?.eligible_dependents?.some(dep => isDependentHealthImputedIncome(dep));

		if (annualEnrollmentProps?.dependentAdultChildWarning &&
			anyDependentHealthImputedIncome) {
			return (
				<Banner banner={{
					type: 'warning',
					label: <WysiwygWithReactLink content={annualEnrollmentProps?.dependentAdultChildWarning} />,
				}} />);
		}

		return null;
	};


	function RenderMedicalNoCoverageReasonOther(formProps: FormikProps<AnnualEnrollmentFormValues>) {
		if (formProps?.values?.declinedMedicalCoverageReason !== medicalDeclineCoverageReasonOther) return null;

		return (
			<>
				<p className='custom-sublabel'>{annualEnrollmentProps.declineMedicalCoverageReasonSelectMessage}</p>
				<FormField
					name='declinedMedicalCoverageReasonOther'
					label={annualEnrollmentProps.declineMedicalCoverageReasonOtherLabel || 'I have coverage through a spouse`s, partner`s, or parent`s employer.'}
					component='input'

					multiline
					rows={5}
					touched={formProps.touched}
					errors={formProps.errors}
					required
				/>
			</>
		);
	}

	function RenderMedicalNoCoverageReason(
		formProps: FormikProps<AnnualEnrollmentFormValues>,
		selectedMedicalPlan: string) {
		if (selectedMedicalPlan && selectedMedicalPlan !== DeclinedValue) return null;

		return (
			<div className='grid-nested'>
				<div className='grid-main-content'>

					<DividerSection greySection>
						<ContentSpacer>
							<h2>{annualEnrollmentProps.declineMedicalCoverageReasonGroupTitle || 'Declined Coverage*'}<span className='red-text'>*</span></h2>
						</ContentSpacer>
						<FormGroup>
							<div className='form-item'>
								<FormField
									name='declinedMedicalCoverageReason'
									label={annualEnrollmentProps.declineMedicalCoverageReasonSelectLabel || 'Please select a reason for declining medical coverage.*'}
									component='input'
									options={declinedMedicalCoverageReasonOptions}
									errors={formProps.errors}
									touched={formProps.touched}
									select
									required
									placeholder={annualEnrollmentProps.declineMedicalCoverageReasonSelectPlaceHolder || 'Please Select A Reason'}
								/>

							</div>
							{RenderMedicalNoCoverageReasonOther(formProps)}
						</FormGroup>
					</DividerSection>
				</div>
			</div>
		);
	}

	const renderFormContent = (formProps: FormikProps<AnnualEnrollmentFormValues>, selectedMedicalPlan: string, selectedDentalPlan: string) => {

		if (showReview) {
			return (
				<AnnualEnrollmentReview
					formProps={formProps}
					previousAction={() => previousStepAction(formProps)}
					medicalExpired={medicalExpired}
					dentalExpired={dentalExpired}
				/>);
		}

		const medicalDisabled = formProps.values.M.length === 0;
		const dentalDisabled = formProps.values.D.length === 0;
		const dependentAgingOut = AEState.AEInfo?.eligible_dependents?.some(dep => isDependentAgingOut(dep));

		return (
			<>
				<div className='grid-main-content'>
					<DividerSection removeDivider sectionText={annualEnrollmentProps?.annualEnrollmentOptionsLabel}>
						<ContentSpacer>
							<div className='rich-text'>
								<p><strong>{annualEnrollmentProps?.planReferenceTitle}</strong></p>
								<p><ExternalLink linkIcon={<PictureAsPdfIcon />} newWindow linkText={annualEnrollmentProps?.staticPlanReferenceName} linkUrl={annualEnrollmentProps.staticPlanReferenceLink} /></p>
								<p><button className={buttonClasses.linkButton} type='button' onClick={() => downloadPdf(comparisonDocLink, 'plan-comparison.pdf')}><PictureAsPdfIcon /><span>{annualEnrollmentProps?.planComparisonDoc}</span></button></p>
							</div>
						</ContentSpacer>
						<ContentSpacer>
							<h3>{annualEnrollmentProps?.participantSelectionLabel}</h3>
							<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps?.participantSelectionDescription }} />
							{dependentAgingOut &&
								<Banner banner={{
									type: 'warning',
									label: <p className='h5-styled'>{annualEnrollmentProps?.dependentAgingOutMessage}</p>,
								}} />}
							{renderDependentAdultChildWarning()}
						</ContentSpacer>
						<FormGroup>
							<ContentSpacer>
								{isPlanSelectionAvailable(AEState?.AEInfo?.offerings?.M?.plans, medicalExpired) && renderCoverage(formProps, AEPlanType.Medical, annualEnrollmentProps?.medicalCoverageLabel)}
							</ContentSpacer>
							{isPlanSelectionAvailable(AEState?.AEInfo?.offerings?.D?.plans, dentalExpired) && renderCoverage(formProps, AEPlanType.Dental, annualEnrollmentProps?.dentalCoverageLabel)}
						</FormGroup>
					</DividerSection>


				</div>
				<div className='grid-fw-content'>
					<div className='grid-nested'>
						<div className='grid-fw-content'>
							<ContentSpacer>
								<div className={classes.coverTableHeader}>
									<h3 className='coverage-table-header-title'>{annualEnrollmentProps?.planSelectionLabel}</h3>
									<div className='coverage-table-header-description'>
										<div className='rich-text'>
											<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps?.planSelectionDescription }} />
										</div>
										<div className='coverage-table-header-tooltip'>
											<span className='coverage-table-header-tooltip-text'>{annualEnrollmentProps?.monthlyPremiumsLabel}</span>
											<Tooltip iconColor={tcsmColors.blue} title={annualEnrollmentProps?.monthlyPremiumsTooltipText} />

										</div>
									</div>
									<WysiwygWithReactLink content={annualEnrollmentProps?.pensionReductionMessage} />
									{
										hasMspPlan &&
										<WysiwygWithReactLink content={annualEnrollmentProps?.mspMessage} />
									}
								</div>

							</ContentSpacer>
						</div>
						<div className='grid-main-content'>
							{
								// current medical plan is no longer offered
								medicalEnrollment && medicalExpired &&
								<ContentSpacer>
									<Banner banner={{
										type: 'warning',
										label: <p className='h5-styled'>{annualEnrollmentProps?.medicalNoLongerOffered?.replace(/{date}/gi, getFormattedDate(AEState?.AEInfo?.ae_status?.effective_date))}</p>,
									}} />
								</ContentSpacer>
							}
							{
								// current dental plan is no longer offered
								dentalEnrollment && dentalExpired &&
								<ContentSpacer>
									<Banner banner={{
										type: 'warning',
										label: <p className='h5-styled'>{annualEnrollmentProps?.dentalNoLongerOffered?.replace(/{date}/gi, getFormattedDate(AEState?.AEInfo?.ae_status?.effective_date))}</p>,
									}} />
								</ContentSpacer>
							}
						</div>
					</div>
					{
						isPlanSelectionAvailable(AEState?.AEInfo?.offerings?.M?.plans, medicalExpired) &&
						<CustomField
							name='medicalPlan'
							label={getPlanLabels(AEPlanType.Medical)}
							component={RadioTable}
							options={getOfferingsOptions(AEState?.AEInfo?.offerings?.M,
								selectedMedicalPlan,
								AEState.tierCalculations?.M?.display_tier,
								medicalExpired && (medicalEnrollment?.name || medicalEnrollment?.code),
								annualEnrollmentProps, AEPlanType.Medical,
								medicalDisabled,
								labelSetting,
								hasMspPlan,
							)}
							formProps={formProps}
							onChange={(e) => onCoverageChange(e, AEPlanType.Medical, formProps)}
						/>
					}

					{RenderMedicalNoCoverageReason(formProps, selectedMedicalPlan)}
					{
						isPlanSelectionAvailable(AEState?.AEInfo?.offerings?.D?.plans, dentalExpired) &&
						<CustomField
							name='dentalPlan'
							label={getPlanLabels(AEPlanType.Dental)}
							component={RadioTable}
							options={getOfferingsOptions(AEState?.AEInfo?.offerings?.D,
								selectedDentalPlan,
								AEState.tierCalculations?.D?.display_tier,
								dentalExpired && (dentalEnrollment?.name || dentalEnrollment?.code),
								annualEnrollmentProps, AEPlanType.Dental,
								dentalDisabled,
								labelSetting)}
							formProps={formProps}
							onChange={(e) => onCoverageChange(e, AEPlanType.Dental, formProps)}
						/>
					}

				</div>
				<div className='grid-main-content'>
					<AnnualEnrollmentSummary
						AEProps={annualEnrollmentProps}
						AEState={AEState}
						selectedDentalPlan={selectedDentalPlan}
						selectedMedicalPlan={selectedMedicalPlan}
						showNotApplicable={showNotApplicableRate}
					/>
					{renderComments(formProps)}
				</div>


			</>
		);
	};

	const getButtonText = () => {
		return showReview ? [labelSettings.submitText, annualEnrollmentProps?.coveragePreviousButtonText] : [annualEnrollmentProps?.coverageContinueButtonText, annualEnrollmentProps?.coveragePreviousButtonText];
	};

	const validationSchema = Yup.object().shape({
		memo: Yup.string().nullable().max(255, annualEnrollmentProps.maxCharacterErrorText || MaxCharacter255),
		specialRequests: Yup.string().nullable().max(255, annualEnrollmentProps.maxCharacterErrorText || MaxCharacter255),
		declinedMedicalCoverageReason: Yup.string().nullable().ensure()
			.when(['medicalPlan'], {
				is: (medicalPlan: string) => {
					return medicalPlan === DeclinedValue;
				},
				then: Yup.string().required(getRequiredText(labelSettings.requiredFormat, annualEnrollmentProps.declineMedicalCoverageReasonSelectLabel || 'Reason')),
			}),
		declinedMedicalCoverageReasonOther: Yup.string().nullable().ensure()
			.when(['declinedMedicalCoverageReason', 'medicalPlan'], {
				is: (declinedMedicalCoverageReason: string[], medicalPlan: string) => {
					return medicalPlan === DeclinedValue && declinedMedicalCoverageReason?.length > 0 && declinedMedicalCoverageReason.includes(medicalDeclineCoverageReasonOther);
				},
				then: Yup.string().required(getRequiredText(labelSettings.requiredFormat, annualEnrollmentProps.declineMedicalCoverageReasonOtherLabel || 'Other Reason')),
			}),
	});

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={(values: AnnualEnrollmentFormValues, helpers: FormikHelpers<AnnualEnrollmentFormValues>) => onSubmit(values, initialValues, helpers)}
			enableReinitialize
			validationSchema={validationSchema}
		>
			{(formProps: FormikProps<AnnualEnrollmentFormValues>) => {
				const selectedMedicalPlan = formProps.values.medicalPlan;
				const selectedDentalPlan = formProps.values.dentalPlan;

				return (
					<>
						<Form className='grid-fw-content grid-nested'>
							<div className='grid-main-content'>
								{
									app === DemographicAppKeys.arc &&
									<ContentSpacer>
										<div dangerouslySetInnerHTML={{ __html: annualEnrollmentProps?.adminAccessMessage }} />
									</ContentSpacer>
								}
								<PageHeader
									headerText={showReview ? annualEnrollmentProps.reviewHeader : annualEnrollmentProps.coverageTitle}
									description={showReview ? annualEnrollmentProps.reviewDescription : annualEnrollmentProps.coverageDescription} />
								<Hidden mdUp>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
									</SideBarContent>
								</Hidden>
								{coverageDisplay}
							</div>
							<Hidden smDown>
								<div className='grid-sidebar'>
									<SideBarContent>
										<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
									</SideBarContent>
								</div>
							</Hidden>
							<FormWrapper
								formikProps={formProps}
								submitText={getButtonText()[0]}
								cancelText={getButtonText()[1]}
								onCancel={() => previousStepAction(formProps)}
								preventButtonDisableValidation={isAnnualEnrollment}
								submitDisabled={showReview && !formProps.values.agreement}>

								<EnrollmentErrors type={EnrollmentTypes.ANNUAL} />

								{renderFormContent(formProps, selectedMedicalPlan, selectedDentalPlan)}

							</FormWrapper>

						</Form>
					</>
				);
			}}
		</Formik>
	);
}